import type { LetterRecommendation } from '@frontend/api-types';
import { useEffect, useFeatureValue, useVisibilityController } from '@frontend/react';
import { mapSuggestion, type HomeScreenTaskSuggestion } from '@frontend/react-domain';
import { useDismissedSuggestionsSlice } from '@frontend/redux';
import { LinkButton, Text, XStack, YStack } from '@frontend/web-react';
import { CardsStack, LetterRecommendationDetailModal } from '@frontend/web/components';
import type { CardItem } from '@frontend/web/components/DomainSpecific/CardsStack';
import { useAnalytics, useListSuggestionsQuery } from '@frontend/web/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { SuggestionCard } from './SuggestionCard';

type SuggestionCardItem = HomeScreenTaskSuggestion & { content: string; id: string };

type LetterRecommendationsContainerProps = {
  layout?: 'horizontal' | 'vertical';
  letterRecommendations?: LetterRecommendation[];
};

export const LetterRecommendationsContainer = ({
  layout = 'horizontal',
  letterRecommendations = [],
}: LetterRecommendationsContainerProps) => {
  const { value: unreadLetterThreshold } = useFeatureValue('unread_letter_threshold');
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { dismissedSuggestions, dismissSuggestion } = useDismissedSuggestionsSlice();
  const [
    isLetterRecommendationDetailModalOpen,
    { close: closeLetterRecommendationDetailModal, open: openLetterRecommendationDetailModal },
  ] = useVisibilityController();
  const [selectedLetterRecommendationId, setSelectedLetterRecommendationId] = useState<string | null>(null);

  const { data: suggestionsData } = useListSuggestionsQuery();

  const suggestions = useMemo(
    () =>
      (suggestionsData ?? [])
        .map(mapSuggestion)
        .filter((s) => !dismissedSuggestions.includes(s.suggestionId))
        .slice(0, 6),
    [suggestionsData, dismissedSuggestions],
  );

  const suggestionCards: SuggestionCardItem[] = useMemo(
    () =>
      suggestions.map((s) => ({
        ...s,
        content: s.shortenedLabel || s.draftDescription,
        id: s.suggestionId,
      })),
    [suggestions],
  );

  const suggestionCardsForStack = suggestionCards as CardItem<(typeof suggestionCards)[0]>[];

  const hasSuggestions = suggestions.length > 0;

  const handleStartTask = useCallback(
    (item: SuggestionCardItem) => {
      track({
        data: { suggestion: item.draftDescription, suggestionId: item.suggestionId },
        event: 'suggestionSelected',
        topic: 'suggestions',
      });
      navigate({
        search: { description: item.draftDescription, suggestionId: item.suggestionId },
        to: '/app/draft-task/create',
      });
    },
    [navigate, track],
  );

  const handleDismissSuggestion = useCallback(
    (item: SuggestionCardItem) => {
      track({
        data: { suggestionId: item.suggestionId },
        event: 'suggestionDismissed',
        topic: 'suggestions',
      });
      dismissSuggestion(item.suggestionId);
    },
    [dismissSuggestion, track],
  );

  const handleLetterRecommendationPress = (id: string) => {
    setSelectedLetterRecommendationId(id);
    openLetterRecommendationDetailModal();
  };

  const handleCloseDetailModal = () => {
    setSelectedLetterRecommendationId(null);
    closeLetterRecommendationDetailModal();
  };

  useEffect(() => {
    if (letterRecommendations?.length && unreadLetterThreshold) {
      // check if member hasn't read last two letters
      const unreadLetters = letterRecommendations.slice(0, unreadLetterThreshold).filter((letter) => !letter.viewedAt);
      if (unreadLetters.length >= unreadLetterThreshold && letterRecommendations[0]) {
        handleLetterRecommendationPress(letterRecommendations[0].id);
      }
    }
  }, [unreadLetterThreshold, letterRecommendations]);

  if (!hasSuggestions) {
    return null;
  }

  return (
    <YStack accessibilityLabel="Letter recommendations container" className="gap-md flex-1">
      <YStack gap="xs">
        <XStack className="w-full items-center justify-between">
          <Text color="primary" type="h3">
            Queued up for you
          </Text>
          {layout === 'horizontal' && suggestions.length > 3 ? (
            <LinkButton
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="rounded-[100px] border-1 bg-cream-300 border-cream-400 px-[12px] py-sm"
              color="secondary"
              label="See all"
              onClick={() => navigate({ to: '/app/letter-recommendation' })}
              textClassName="font-normal text-textTertiary"
              type="p-sm"
            />
          ) : null}
        </XStack>
      </YStack>
      {layout === 'vertical' ? (
        <YStack className="gap-sm">
          {suggestionCards.map((card) => (
            <SuggestionCard
              key={card.id}
              onClick={() => handleStartTask(card)}
              onDismiss={() => handleDismissSuggestion(card)}
              onSave={() => handleStartTask(card)}
              suggestion={card}
            />
          ))}
        </YStack>
      ) : (
        <CardsStack data={suggestionCardsForStack} onDismiss={handleDismissSuggestion} onStartTask={handleStartTask} />
      )}
      {selectedLetterRecommendationId ? (
        <LetterRecommendationDetailModal
          isOpen={!!selectedLetterRecommendationId && isLetterRecommendationDetailModalOpen}
          letterRecommendationId={selectedLetterRecommendationId}
          onClose={handleCloseDetailModal}
        />
      ) : null}
    </YStack>
  );
};
